import { PayloadAction, createSlice } from '@reduxjs/toolkit'

import { AppDispatch, GetState, RootState } from '../store'

import {
  getTaskCategories,
  createTaskCategory,
  deleteTaskCategory,
  updateTaskCategory
} from '../lib/api'
import { AddErrorAlert, AddSuccessAlert } from './alert'
import { sortByFlowOrder } from '@SonarCX/pages/Settings/TaskSettings/DraggableSetting/helpers'

type CategoryState = {
  categories: Record<string, APICategory>
  categoriesValues: APICategory[]
  publicCategoriesValues: APICategory[]
  loaded: boolean
  loading: boolean
  error: any
}

const initialState: CategoryState = {
  categories: {},
  categoriesValues: [],
  publicCategoriesValues: [],
  loaded: false,
  loading: false,
  error: null
}

export const categorySlice = createSlice({
  name: 'category',
  initialState,
  reducers: {
    loading: (state: CategoryState) => {
      state.loading = true
    },

    load: (
      state: CategoryState,
      action: PayloadAction<Record<string, APICategory>>
    ) => {
      const { payload } = action
      state.categories = payload

      const asArray = Object.values(payload)
      state.categoriesValues = sortByFlowOrder(asArray)
      state.publicCategoriesValues = sortByFlowOrder(
        asArray.filter(cat => !Boolean(cat.private))
      )

      state.loaded = true
      state.loading = false
    },

    add: (state: CategoryState, action: PayloadAction<APICategory>) => {
      const { id } = action.payload
      state.categories[id] = action.payload

      const asArray = Object.values(state.categories)
      state.categoriesValues = sortByFlowOrder(asArray)
      state.publicCategoriesValues = sortByFlowOrder(
        asArray.filter(cat => !Boolean(cat.private))
      )
    },

    remove: (state: CategoryState, action: PayloadAction<string>) => {
      delete state.categories[action.payload]
      const asArray = Object.values(state.categories)
      state.categoriesValues = sortByFlowOrder(asArray)
      state.publicCategoriesValues = sortByFlowOrder(
        asArray.filter(cat => !Boolean(cat.private))
      )
    },

    update: (state: CategoryState, action: PayloadAction<APICategory>) => {
      const { id } = action.payload
      state.categories[id] = action.payload
      const asArray = Object.values(state.categories)
      state.categoriesValues = sortByFlowOrder(asArray)
      state.publicCategoriesValues = sortByFlowOrder(
        asArray.filter(cat => !Boolean(cat.private))
      )
    },

    failed: (state: CategoryState, action: PayloadAction<any>) => {
      state.error = action.payload
      state.loading = false
      state.loaded = true
    },

    patch: (
      state: CategoryState,
      action: PayloadAction<Record<string, Partial<APICategory>>>
    ) => {
      const data = action.payload

      for (const entry of Object.entries(data)) {
        state.categories[entry[0]] = {
          ...state.categories[entry[0]],
          ...entry[1]
        }
      }

      const updated = state.publicCategoriesValues.map(status => {
        return {
          ...status,
          ...(data[status.id] ?? {})
        }
      })

      state.categoriesValues = sortByFlowOrder(Object.values(state.categories))
      state.publicCategoriesValues = sortByFlowOrder(updated)
    }
  }
})

const { loading, load, add, remove, update, failed, patch } =
  categorySlice.actions

export const fetchCategories = () => {
  return async (dispatch: AppDispatch) => {
    dispatch(loading())
    try {
      const categories = await getTaskCategories()
      const toSet = {}
      categories.forEach(cat => {
        toSet[cat.id] = cat
      })
      dispatch(load(toSet))
    } catch (error) {
      dispatch(failed(error))
    }
  }
}

export const createCategory = (cat: {
  name: string | null
  private: boolean
  flow_order: number | null
}) => {
  return async (dispatch: AppDispatch) => {
    const created = await createTaskCategory(cat)
    dispatch(add(created))
  }
}

export const deleteCategory = (id: string) => {
  return async (dispatch: AppDispatch) => {
    await deleteTaskCategory(id)
    dispatch(remove(id))
  }
}

export const updateCategory = (data: APICategory) => {
  const { id, total_tasks, owner_id, total_open, ...rest } = data
  return async (dispatch: AppDispatch) => {
    const updated = await updateTaskCategory(id, rest)
    dispatch(update(updated))
  }
}

export const patchCategories = (data: Partial<APICategory>[]) => {
  return async (dispatch: AppDispatch, getState: GetState) => {
    const { publicCategoriesValues } = selectCategory(getState())

    try {
      const toUpdate: Record<string, APICategory> = {}

      data.forEach(item => {
        const { id, ...props } = item
        const current = publicCategoriesValues.find(status => status.id === id)

        if (!current) {
          return
        }

        // Check if any of the updates are new
        const hasUpdate = Object.entries(props).some(
          ([key, val]) => current[key] !== val
        )

        if (hasUpdate) {
          toUpdate[id] = { ...current, ...item }
        }
      })

      // Send updates to server
      Object.values(toUpdate).forEach(status => {
        const { id, total_tasks, owner_id, total_open, ...rest } = status
        updateTaskCategory(id, rest)
      })

      dispatch(patch(toUpdate))

      dispatch(AddSuccessAlert('Changes saved.'))
    } catch (err) {
      dispatch(AddErrorAlert({ err }))
    }
  }
}

export const selectCategory = (state: RootState) => state.category

export default categorySlice.reducer
